import { useCallback, useEffect, useRef, useState } from "react";
import { getCampaignFocus, getCampaignTabIndex } from "type/campaign-tab";

/**
 * The campaign page's tab and Market focus, opened from a link such as `?tab=settings&focus=market`.
 *
 * Focus is held as state rather than read off the URL so the scroll happens once per link: Settings
 * calls `onMarketFocused` after scrolling, and switching back to the tab later doesn't scroll again.
 *
 * A link to the page already open changes only the query string, without remounting, so a changed
 * query string is followed. The first render is not a change: on mount the child's effect runs before
 * this one, so resyncing then would undo the focus Settings has just cleared.
 */
export const useCampaignLink = (search: string, tabLabels: string[]) => {
	const [tabIndex, setTabIndex] = useState(() => getCampaignTabIndex(search, tabLabels) ?? 0);
	const [focusMarket, setFocusMarket] = useState(() => getCampaignFocus(search) === "market");
	const onMarketFocused = useCallback(() => setFocusMarket(false), []);

	const followedSearch = useRef(search);
	useEffect(() => {
		if (search === followedSearch.current) {
			return;
		}
		followedSearch.current = search;
		const index = getCampaignTabIndex(search, tabLabels);
		if (index !== undefined) {
			setTabIndex(index);
		}
		setFocusMarket(getCampaignFocus(search) === "market");
	}, [search, tabLabels]);

	return { tabIndex, setTabIndex, focusMarket, onMarketFocused };
};
